import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { url } from '../../../../env';
import { AppButton, AppTextField, AppSelectField, AppIconButton } from '../../../../Components/FormKit';
import usePermissions from '../../../../Hooks/usePermissions';
import { ROLE_OPTIONS, ROLE_DESCRIPTIONS, labelOf } from './roleConfig';
import { createUserModalCSS, createUserDialogStyle } from './UsersManagement.css';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 650);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 650);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const CreateUserModal = ({ visible, onHide, onCreated, toast }) => {
    const isMobile = useIsMobile();
    const { isAdminPei } = usePermissions();
    const adminPei = isAdminPei();

    const allowedRoles = adminPei
        ? ROLE_OPTIONS
        : ROLE_OPTIONS.filter((r) => r.value === '4');

    const initialForm = {
        name: '',
        email: '',
        type_user: adminPei ? '' : '4',
        send_invitation: true,
    };

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [passwordVisible, setPasswordVisible] = useState(true);

    useEffect(() => {
        if (visible) {
            setForm(initialForm);
            setErrors({});
            setResult(null);
            setPasswordVisible(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const set = (k, v) => {
        setForm((prev) => ({ ...prev, [k]: v }));
        if (errors[k]) setErrors((prev) => ({ ...prev, [k]: null }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'El nombre es obligatorio.';
        if (!form.email.trim()) e.email = 'El email es obligatorio.';
        else if (!isValidEmail(form.email.trim())) e.email = 'Email inválido.';
        if (!form.type_user) e.type_user = 'Selecciona un rol.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        try {
            const res = await axios.post(`${url}/users`, {
                name: form.name.trim(),
                email: form.email.trim(),
                type_user: form.type_user,
                send_invitation: form.send_invitation,
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            setResult(res.data);

            toast?.current?.show({
                severity: 'success',
                summary: 'Usuario creado',
                detail: `${res.data?.user?.name} fue creado correctamente.`,
                life: 3000,
            });

            if (res.data?.invitation_error) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Invitación no enviada',
                    detail: res.data.invitation_error,
                    life: 5000,
                });
            }

            onCreated?.();
        } catch (err) {
            console.error(err);
            const status = err?.response?.status;
            const data = err?.response?.data ?? {};

            // Errores de validación de Laravel
            if (status === 422 && data.errors) {
                const fieldErrors = {};
                Object.entries(data.errors).forEach(([key, msgs]) => {
                    fieldErrors[key] = Array.isArray(msgs) ? msgs[0] : String(msgs);
                });
                setErrors(fieldErrors);
            }

            toast?.current?.show({
                severity: 'error',
                summary: 'No se pudo crear el usuario',
                detail: data.message ?? 'Verifica los datos e inténtalo de nuevo.',
                life: 4000,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const copyPassword = () => {
        if (!result?.temp_password) return;
        navigator.clipboard.writeText(result.temp_password);
        toast?.current?.show({
            severity: 'success',
            summary: 'Contraseña copiada',
            life: 1500,
        });
    };

    const copyCredentials = () => {
        if (!result) return;
        const text = `Usuario: ${result.user.email}\nContraseña: ${result.temp_password}`;
        navigator.clipboard.writeText(text);
        toast?.current?.show({
            severity: 'success',
            summary: 'Credenciales copiadas',
            life: 1500,
        });
    };

    const createAnother = () => {
        setResult(null);
        setForm(initialForm);
        setErrors({});
        setPasswordVisible(true);
    };

    const renderForm = () => (
        <>
            <header className="cum-header">
                <div>
                    <h2 className="cum-title">Crear usuario</h2>
                    <p className="cum-subtitle">
                        {adminPei
                            ? 'Crea un usuario interno y elige cómo entregarle el acceso.'
                            : 'Invita a un nuevo Vendedor Trim a tu equipo.'}
                    </p>
                </div>
                <button type="button" onClick={onHide} className="cum-close" aria-label="Cerrar">
                    <i className="pi pi-times" />
                </button>
            </header>

            <div className="cum-grid">
                <AppTextField
                    label="Nombre completo"
                    required
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    invalid={!!errors.name}
                    errorMessage={errors.name}
                    placeholder="Ej. Ana López"
                />
                <AppTextField
                    label="Email"
                    required
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    invalid={!!errors.email}
                    errorMessage={errors.email}
                    placeholder="ana.lopez@empresa.com"
                />

                {adminPei ? (
                    <AppSelectField
                        label="Rol"
                        required
                        value={form.type_user}
                        options={allowedRoles}
                        onChange={(e) => set('type_user', e.value)}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Selecciona un rol"
                        invalid={!!errors.type_user}
                        errorMessage={errors.type_user}
                        helperText={form.type_user ? ROLE_DESCRIPTIONS[form.type_user] : ' '}
                        style={{ gridColumn: '1 / -1' }}
                    />
                ) : (
                    <div className="cum-readonly" style={{ gridColumn: '1 / -1' }}>
                        <span className="cum-readonly-label">Rol</span>
                        <span className="cum-readonly-value">Vendedor Trim</span>
                        <span className="cum-readonly-help">{ROLE_DESCRIPTIONS['4']}</span>
                    </div>
                )}
            </div>

            <div className="cum-switch-row">
                <div className="cum-switch-text">
                    <span className="cum-switch-title">Enviar invitación al correo</span>
                    <span className="cum-switch-help">
                        Si está activado, el usuario recibirá un email con un enlace para activar su
                        cuenta y la contraseña temporal. Si no, podrás copiarla aquí mismo para
                        entregársela personalmente.
                    </span>
                </div>
                <InputSwitch
                    checked={form.send_invitation}
                    onChange={(e) => set('send_invitation', e.value)}
                />
            </div>

            <div className="cum-footer">
                <AppButton label="Cancelar" variant="secondary" onClick={onHide} disabled={submitting} />
                <AppButton
                    label="Crear usuario"
                    variant="primary"
                    onClick={onSubmit}
                    loading={submitting}
                    disabled={submitting}
                />
            </div>
        </>
    );

    const renderResult = () => (
        <>
            <header className="cum-header">
                <div>
                    <h2 className="cum-title">
                        <i className="pi pi-check-circle" style={{ color: '#00BA6E', marginRight: 8 }} />
                        Usuario creado
                    </h2>
                    <p className="cum-subtitle">
                        {result?.user?.name} ({labelOf(result?.user?.type_user)}) fue agregado correctamente.
                    </p>
                </div>
                <button type="button" onClick={onHide} className="cum-close" aria-label="Cerrar">
                    <i className="pi pi-times" />
                </button>
            </header>

            <div className="cum-credentials">
                <div className="cum-cred-row">
                    <span className="cum-cred-label">Email</span>
                    <span className="cum-cred-value">{result?.user?.email}</span>
                </div>

                <div className="cum-cred-row">
                    <span className="cum-cred-label">Contraseña temporal</span>
                    <div className="cum-pass-row">
                        <span className="cum-pass-value">
                            {passwordVisible ? result?.temp_password : '••••••••••••'}
                        </span>
                        <AppIconButton
                            icon={passwordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'}
                            title={passwordVisible ? 'Ocultar' : 'Mostrar'}
                            onClick={() => setPasswordVisible((v) => !v)}
                            variant="secondary"
                            round
                        />
                        <AppIconButton
                            icon="pi pi-copy"
                            title="Copiar contraseña"
                            onClick={copyPassword}
                            variant="secondary"
                            round
                        />
                    </div>
                </div>

                <button type="button" className="cum-copy-all" onClick={copyCredentials}>
                    <i className="pi pi-clone" />
                    <span>Copiar email y contraseña</span>
                </button>
            </div>

            {result?.invitation_sent && (
                <div className="cum-callout cum-callout--success">
                    <i className="pi pi-envelope" />
                    <span>
                        Se envió un correo de invitación a <strong>{result.user.email}</strong> con
                        el enlace para activar la cuenta y esta contraseña temporal.
                    </span>
                </div>
            )}

            {!result?.invitation_sent && !result?.invitation_error && (
                <div className="cum-callout">
                    <i className="pi pi-info-circle" />
                    <span>
                        No se envió email. Copia las credenciales y entrégaselas al usuario por
                        un canal seguro. Recomiéndale cambiar la contraseña al iniciar sesión.
                    </span>
                </div>
            )}

            {result?.invitation_error && (
                <div className="cum-callout cum-callout--warn">
                    <i className="pi pi-exclamation-triangle" />
                    <span>{result.invitation_error}</span>
                </div>
            )}

            <div className="cum-footer">
                <AppButton label="Crear otro" variant="secondary" onClick={createAnother} />
                <AppButton label="Cerrar" variant="primary" onClick={onHide} />
            </div>
        </>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            draggable={false}
            resizable={false}
            dismissableMask={!result}
            closeOnEscape={!result}
            style={createUserDialogStyle(isMobile)}
            contentStyle={{
                padding: '20px',
                background: '#fefefe',
                borderRadius: isMobile ? '0.8rem 0.8rem 0 0' : '0.8rem',
            }}
            showHeader={false}
        >
            <div css={createUserModalCSS()} className="fk-root">
                {result ? renderResult() : renderForm()}
            </div>
        </Dialog>
    );
};

export default CreateUserModal;
